import { useState, useEffect } from 'react';
import { getCampaigns, createCampaign, updateCampaign, deleteCampaign, Campaign } from '../services/campaignsService';
import { formatDate } from '../utils/dateUtils';
import { getErrorMessage } from '../utils/getErrorMessage';
import '../styles/FormPages.css';
import '../styles/ListPages.css';

export function CampaignsEditor() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    campaignName: '',
    startDate: '',
    endDate: '',
    goalAmount: '',
    description: ''
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await getCampaigns();
      setCampaigns(data);
      setError('');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load campaigns'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const payload = {
        campaignName: formData.campaignName,
        startDate: formData.startDate,
        endDate: formData.endDate,
        goalAmount: formData.goalAmount ? parseFloat(formData.goalAmount) : undefined,
        description: formData.description
      };

      if (isEditing && editingId) {
        await updateCampaign(editingId, payload);
        setSuccess('Campaign updated successfully');
      } else {
        await createCampaign(payload);
        setSuccess('Campaign created successfully');
      }

      setFormData({ campaignName: '', startDate: '', endDate: '', goalAmount: '', description: '' });
      setIsEditing(false);
      setEditingId(null);
      loadCampaigns();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to save campaign'));
    }
  };

  const handleEdit = (campaign: Campaign) => {
    setFormData({
      campaignName: campaign.campaignName,
      startDate: campaign.startDate.split('T')[0],
      endDate: campaign.endDate.split('T')[0],
      goalAmount: campaign.goalAmount?.toString() || '',
      description: campaign.description || ''
    });
    setIsEditing(true);
    setEditingId(campaign.campaignID);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This will hide it from all dropdowns.`)) {
      return;
    }

    try {
      await deleteCampaign(id);
      setSuccess('Campaign deleted successfully (soft delete)');
      loadCampaigns();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to delete campaign'));
    }
  };

  const handleCancel = () => {
    setFormData({ campaignName: '', startDate: '', endDate: '', goalAmount: '', description: '' });
    setIsEditing(false);
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  return (
    <div>
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>
      )}

      {/* Add/Edit Form */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3>{isEditing ? 'Edit Campaign' : 'Add New Campaign'}</h3>
        <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="campaignName">Campaign Name *</label>
              <input
                type="text"
                id="campaignName"
                value={formData.campaignName}
                onChange={(e) => setFormData({ ...formData, campaignName: e.target.value })}
                placeholder="e.g., Annual Fund 2025, Holiday Appeal"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="startDate">Start Date *</label>
              <input
                type="date"
                id="startDate"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date *</label>
              <input
                type="date"
                id="endDate"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="goalAmount">Goal Amount ($)</label>
              <input
                type="number"
                id="goalAmount"
                value={formData.goalAmount}
                onChange={(e) => setFormData({ ...formData, goalAmount: e.target.value })}
                placeholder="50000"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this campaign"
              rows={3}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="submit" className="button-primary">
              {isEditing ? 'Update Campaign' : 'Add Campaign'}
            </button>
            {isEditing && (
              <button type="button" className="button-secondary" onClick={handleCancel}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Campaigns List */}
      <div className="card">
        <h3>Current Campaigns</h3>
        {loading ? (
          <p>Loading...</p>
        ) : campaigns.length === 0 ? (
          <p style={{ color: '#999', fontStyle: 'italic' }}>No campaigns found.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Campaign Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Goal Amount</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(campaign => (
                  <tr key={campaign.campaignID}>
                    <td><strong>{campaign.campaignName}</strong></td>
                    <td>{formatDate(campaign.startDate)}</td>
                    <td>{formatDate(campaign.endDate)}</td>
                    <td>{campaign.goalAmount ? `$${campaign.goalAmount.toLocaleString()}` : '-'}</td>
                    <td>{campaign.description || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleEdit(campaign)}
                          className="button-link"
                          style={{ color: '#5B2C86' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(campaign.campaignID, campaign.campaignName)}
                          className="button-link"
                          style={{ color: '#D32F2F' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
